import {
  Decimal,
  EVIDENCE_TYPES,
  type CreditDecisionOutcome,
  type EvidenceType,
  type MoneyJson,
} from '@paguz/contracts';
import { Badge, Button, cn, formatCop, MoneyText, toast } from '@paguz/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckIcon, FilePlus2Icon, RotateCcwIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
import { useState } from 'react';
import { describeError } from '../../api/client.ts';
import { api, queryKeys } from '../../api/endpoints.ts';
import {
  DECISION_REASON_CODES,
  type AdminApplicationDetail,
} from '../../api/origination-schemas.ts';
import { ConfirmActionDialog, SummaryRows } from '../../components/confirm-dialog.tsx';
import { Checkbox, Field, Select, Textarea } from '../../components/form-controls.tsx';
import { DataList, Panel } from '../../components/page.tsx';
import { formatDateTime, formatRate } from '../../lib/format.ts';
import { EVIDENCE_LABEL } from '../../lib/labels.ts';
import { useCan } from '../../lib/session.ts';
import { MoneyField } from '../config/setting-editor.tsx';
import { OUTCOME_LABEL } from './labels.ts';

const OUTCOMES: readonly CreditDecisionOutcome[] = ['APPROVED', 'COUNTER_OFFER', 'REJECTED'];
const OUTCOME_ICON = {
  APPROVED: CheckIcon,
  COUNTER_OFFER: RotateCcwIcon,
  REJECTED: XIcon,
} as const;
const REASON_LABEL = new Map<string, string>(
  Object.values(DECISION_REASON_CODES).flatMap((list) => list.map(([c, l]) => [c, l] as const)),
);

/**
 * Panel fijo de decisión (`06-backoffice.md` §4.2): aprobar, contraoferta o rechazar con motivos
 * codificados + comentario, doble confirmación y auditoría. Solo el analista decide.
 */
export function DecisionPanel({ detail }: { detail: AdminApplicationDetail }) {
  const app = detail.application;
  const canDecide = useCan('applications:decide');
  const canOperate = useCan('applications:operate');
  const waiting = app.status === 'SUBMITTED' && app.phase === 'DECISION';

  if (app.decision) return <DecisionSummary detail={detail} />;
  return (
    <div className="flex flex-col gap-4">
      {waiting && canDecide ? (
        <DecisionForm detail={detail} />
      ) : (
        <Panel title="Decisión">
          <p className="text-sm text-muted-foreground">
            {waiting
              ? 'Tu rol puede ver el expediente pero no decidir.'
              : 'El expediente aún no está en decisión: el Asesor IA sigue reuniendo información con el cliente.'}
          </p>
        </Panel>
      )}
      {canOperate && <OtherActions detail={detail} waiting={waiting} />}
    </div>
  );
}

function DecisionSummary({ detail }: { detail: AdminApplicationDetail }) {
  const d = detail.application.decision;
  if (!d) return null;
  const offer = detail.application.offer;
  return (
    <Panel title="Decisión registrada">
      <div className="flex flex-col gap-4">
        <Badge
          variant={
            d.outcome === 'APPROVED' ? 'success' : d.outcome === 'REJECTED' ? 'danger' : 'warning'
          }
          className="w-fit text-sm"
        >
          {
            { APPROVED: 'Aprobado', COUNTER_OFFER: 'Contraoferta', REJECTED: 'Rechazado' }[
              d.outcome
            ]
          }
        </Badge>
        <DataList
          columns={1}
          items={[
            ...(d.approvedLimit
              ? [
                  {
                    label: 'Cupo',
                    value: (
                      <MoneyText
                        value={d.approvedLimit.amount}
                        size="md"
                        className="font-semibold"
                      />
                    ),
                  },
                ]
              : []),
            ...(d.rateEA ? [{ label: 'Tasa', value: formatRate(d.rateEA) }] : []),
            ...(d.maxInstallments ? [{ label: 'Cuotas máximas', value: d.maxInstallments }] : []),
            {
              label: 'Decidió',
              value: d.decidedBy === 'POLICY_ENGINE' ? 'Motor de decisión (política)' : 'Analista',
              hint: formatDateTime(d.decidedAt),
            },
            {
              label: 'Motivos',
              value: d.reasonCodes.map((c) => REASON_LABEL.get(c) ?? c).join(' · '),
            },
            { label: 'Comentario', value: d.rationale },
            ...(offer
              ? [
                  {
                    label: 'Oferta',
                    value: `Presentada al cliente; vence ${formatDateTime(offer.expiresAt)}`,
                  },
                ]
              : []),
          ]}
        />
        {detail.application.nextSteps.map((s) => (
          <p key={s} className="rounded-xl bg-muted px-3 py-2 text-sm">
            {s}
          </p>
        ))}
      </div>
    </Panel>
  );
}

function DecisionForm({ detail }: { detail: AdminApplicationDetail }) {
  const app = detail.application;
  const queryClient = useQueryClient();
  const product = useQuery({
    queryKey: queryKeys.setting('product'),
    queryFn: () => api.getSetting('product'),
  });
  const suggested =
    detail.recommendation?.recommendedLimit ?? detail.cashFlow?.suggestedLimit ?? null;
  const [outcome, setOutcome] = useState<CreditDecisionOutcome>(
    detail.recommendation?.recommendation === 'REJECT' ? 'REJECTED' : 'APPROVED',
  );
  const [limit, setLimit] = useState<MoneyJson>(suggested ?? { amount: '0.00', currency: 'COP' });
  const [installments, setInstallments] = useState(12);
  const [codes, setCodes] = useState<string[]>([]);
  const [confirming, setConfirming] = useState(false);

  const p = product.data?.value;
  const allowed = p?.allowedInstallments ?? [1, 2, 3, 6, 12];
  const needsLimit = outcome !== 'REJECTED';
  const limitError =
    needsLimit && p
      ? new Decimal(limit.amount).lt(p.limitMin.amount) ||
        new Decimal(limit.amount).gt(p.limitMax.amount)
        ? `Entre ${formatCop(p.limitMin.amount)} y ${formatCop(p.limitMax.amount)}.`
        : outcome === 'COUNTER_OFFER' && limit.amount === suggested?.amount
          ? 'La contraoferta debe cambiar el cupo sugerido.'
          : undefined
      : undefined;
  const valid = codes.length > 0 && !limitError && (!needsLimit || new Decimal(limit.amount).gt(0));

  const mutation = useMutation({
    mutationFn: (comment: string) =>
      api.decideApplication(app.id, {
        outcome,
        ...(needsLimit && { limit, maxInstallments: installments, productId: app.productCode }),
        reasonCodes: codes,
        comment,
      }),
    onSuccess: (result) => {
      if (result.detail) queryClient.setQueryData(queryKeys.application(app.id), result.detail);
      else void queryClient.invalidateQueries({ queryKey: queryKeys.application(app.id) });
      void queryClient.invalidateQueries({ queryKey: ['applications'] });
      void queryClient.invalidateQueries({ queryKey: ['audit'] });
      toast.success(result.message);
    },
  });

  return (
    <Panel
      title="Decisión del analista"
      description="El motor recomienda; tú decides. Queda en auditoría."
    >
      <div className="flex flex-col gap-5">
        <div
          role="radiogroup"
          aria-label="Resultado"
          className="grid grid-cols-3 gap-1 rounded-xl bg-muted p-1"
        >
          {OUTCOMES.map((o) => {
            const Icon = OUTCOME_ICON[o];
            return (
              <button
                key={o}
                type="button"
                role="radio"
                aria-checked={outcome === o}
                onClick={() => {
                  setOutcome(o);
                  setCodes([]);
                }}
                className={cn(
                  'flex items-center justify-center gap-1 rounded-lg px-2 py-2 text-sm font-semibold text-muted-foreground transition',
                  outcome === o && 'bg-background text-primary shadow-sm',
                )}
              >
                <Icon className="size-4" /> {OUTCOME_LABEL[o]}
              </button>
            );
          })}
        </div>

        {needsLimit && (
          <>
            <MoneyField
              label={outcome === 'COUNTER_OFFER' ? 'Cupo de la contraoferta' : 'Cupo aprobado'}
              value={limit}
              onChange={setLimit}
              error={limitError}
              hint={suggested ? `Sugerido por el motor: ${formatCop(suggested.amount)}` : undefined}
            />
            <div className="grid grid-cols-2 gap-3">
              <Field label="Cuotas máximas" htmlFor="dec-inst">
                <Select
                  id="dec-inst"
                  value={installments}
                  onChange={(e) => {
                    setInstallments(Number(e.target.value));
                  }}
                >
                  {allowed.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Tasa del producto"
                htmlFor="dec-rate"
                hint="Fija por producto (≤ usura)"
              >
                <p id="dec-rate" className="flex h-10 items-center text-sm font-semibold">
                  {p ? formatRate(p.rateEA) : '—'}
                </p>
              </Field>
            </div>
          </>
        )}

        <fieldset>
          <legend className="mb-2 text-sm font-semibold text-foreground">
            Motivos codificados
          </legend>
          <div className="flex flex-col gap-1.5">
            {DECISION_REASON_CODES[outcome].map(([code, label]) => (
              <Checkbox
                key={code}
                label={label}
                description={<code className="font-mono text-xs">{code}</code>}
                checked={codes.includes(code)}
                onCheckedChange={(on) => {
                  setCodes((prev) => (on ? [...prev, code] : prev.filter((c) => c !== code)));
                }}
              />
            ))}
          </div>
        </fieldset>

        <Button
          variant={outcome === 'REJECTED' ? 'destructive' : 'highlight'}
          size="lg"
          disabled={!valid}
          onClick={() => {
            setConfirming(true);
          }}
        >
          <ShieldCheckIcon /> Registrar decisión: {OUTCOME_LABEL[outcome].toLowerCase()}
        </Button>
      </div>

      <ConfirmActionDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={`${OUTCOME_LABEL[outcome]} el expediente de ${detail.applicant.fullName}`}
        description="La decisión dispara origination.application.decided y el Asesor IA se la comunica al cliente."
        confirmWord={app.id}
        destructive={outcome === 'REJECTED'}
        confirmLabel="Registrar decisión"
        reasonLabel="Comentario para el expediente (queda en auditoría)"
        reasonPlaceholder="Resume por qué decides así…"
        summary={
          <SummaryRows
            rows={[
              ['Resultado', OUTCOME_LABEL[outcome]],
              ...(needsLimit
                ? ([
                    ['Cupo', formatCop(limit.amount)],
                    ['Cuotas máximas', String(installments)],
                  ] as [string, string][])
                : []),
              ['Motivos', codes.map((c) => REASON_LABEL.get(c) ?? c).join(' · ')],
            ]}
          />
        }
        onConfirm={async (comment) => {
          await mutation.mutateAsync(comment);
        }}
      />
    </Panel>
  );
}

function OtherActions({ detail, waiting }: { detail: AdminApplicationDetail; waiting: boolean }) {
  const app = detail.application;
  const queryClient = useQueryClient();
  const [docType, setDocType] = useState<EvidenceType>('BANK_STATEMENT');
  const [message, setMessage] = useState('');
  const [returning, setReturning] = useState(false);
  const [phase, setPhase] = useState<'EVIDENCE' | 'CASH_FLOW'>('EVIDENCE');
  const onDone = (next: AdminApplicationDetail, text: string) => {
    queryClient.setQueryData(queryKeys.application(app.id), next);
    void queryClient.invalidateQueries({ queryKey: ['applications'] });
    toast.success(text);
  };
  const request = useMutation({
    mutationFn: () => api.requestDocument(app.id, { type: docType, message: message.trim() }),
    onSuccess: (next) => {
      setMessage('');
      onDone(next, 'Solicitud enviada; el agente le recordará al cliente');
    },
    onError: (error) => toast.error(describeError(error)),
  });
  const back = useMutation({
    mutationFn: (reason: string) => api.returnToPhase(app.id, { phase, reason }),
    onSuccess: (next) => {
      onDone(next, 'Expediente devuelto a la fase');
    },
  });

  return (
    <Panel title="Otras acciones">
      <div className="flex flex-col gap-4">
        <Field label="Solicitar documento adicional" htmlFor="req-type">
          <Select
            id="req-type"
            value={docType}
            onChange={(e) => {
              setDocType(e.target.value as EvidenceType);
            }}
          >
            {EVIDENCE_TYPES.codes.map((c) => (
              <option key={c} value={c}>
                {EVIDENCE_LABEL[c]}
              </option>
            ))}
          </Select>
        </Field>
        <Textarea
          aria-label="Mensaje para el cliente"
          placeholder="Mensaje que el agente le enviará al cliente…"
          value={message}
          onChange={(e) => {
            setMessage(e.target.value);
          }}
        />
        <Button
          variant="outline"
          disabled={message.trim().length < 5 || request.isPending}
          onClick={() => {
            request.mutate();
          }}
        >
          <FilePlus2Icon /> Solicitar documento
        </Button>
        {waiting && (
          <Button
            variant="ghost"
            onClick={() => {
              setReturning(true);
            }}
          >
            <RotateCcwIcon /> Devolver a una fase anterior
          </Button>
        )}
      </div>
      <ConfirmActionDialog
        open={returning}
        onOpenChange={setReturning}
        title="Devolver el expediente"
        description="El Asesor IA retoma la conversación con el cliente en esa fase, sin perder lo ya reunido."
        confirmLabel="Devolver"
        extra={
          <Field label="Fase" htmlFor="ret-phase">
            <Select
              id="ret-phase"
              value={phase}
              onChange={(e) => {
                setPhase(e.target.value as 'EVIDENCE' | 'CASH_FLOW');
              }}
            >
              <option value="EVIDENCE">Evidencias</option>
              <option value="CASH_FLOW">Flujo de caja</option>
            </Select>
          </Field>
        }
        onConfirm={async (reason) => {
          await back.mutateAsync(reason);
        }}
      />
    </Panel>
  );
}
